import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import StepWizard from "react-step-wizard";
import {
	StepPersonalCode,
	StepVerification,
	StepSuccess
} from "../Shared/SmartIdWizardSteps";
import { SmartIdWizardErrorHandler } from "../Shared/SmartIdWizardErrorHandler";
import { useSmartIdSessionPolling } from "../Shared/useSmartIdSessionPolling";
import { SMART_ID_POLLING_RESULT } from "../Shared/smartIdConstants";
import { SmartIdProfileUpdateStep } from "./SmartIdProfileUpdateStep";
import { NO_TRANSITIONS } from "components/tools/wizards/wizardTransitions";

export function SmartIdAddWizard(props) {
	const [wizard, setWizard] = useState(null);
	const [personalCodeEntryError, setPersonalCodeEntryError] = useState("");
	const history = useHistory();
	const isMountedRef = useRef(true);

	useEffect(() => {
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const {
		pin,
		sessionPollingError,
		isAuthenticating,
		startHybridSession,
		pollHybridSession,
		addSmartIdToAccount
	} = useSmartIdSessionPolling();

	const handlePersonalCodeSubmit = (validPersonalCode) => {
		startSmartIdVerification(validPersonalCode);
	};

	const startSmartIdVerification = async (validPersonalCode) => {
		setPersonalCodeEntryError("");
		const result = await startHybridSession(validPersonalCode);

		if (!result.success) {
			setPersonalCodeEntryError(result.error);
		} else {
			wizard.nextStep(); // Go to Step 2: Verification
			await startPollingSessionStep();
		}
	};

	const startPollingSessionStep = async () => {
		if (!isMountedRef.current) {
			console.debug("SmartIdAddWizard: Component unmounted, stopping polling");
			return;
		}

		const result = await pollHybridSession();

		if (result === SMART_ID_POLLING_RESULT.SUCCESS) {
			const addResult = await addSmartIdToAccount();
			if (addResult.status === SMART_ID_POLLING_RESULT.SUCCESS) {
				wizard.goToStep(addResult.personalInfo ? profileUpdateStepIndex : successStepIndex);
			}
		} else if (result === SMART_ID_POLLING_RESULT.CONTINUE) {
			await startPollingSessionStep(); // Continue polling
		}
		// For "expired" or "error", the hook already sets sessionPollingError
	};

	const handleErrorNavigation = () => {
		if (props.onNavigateToProfile) {
			props.onNavigateToProfile();
		} else {
			history.push('/profileInformation');
		}
	};

	return (
		<SmartIdWizardErrorHandler
			error={sessionPollingError}
			onPrimaryButtonClick={handleErrorNavigation}
			buttonTextKey="card_replacement_from_profile_page_return_btn"
		>
			<StepWizard instance={setWizard} transitions={NO_TRANSITIONS}>
				<StepPersonalCode
					onPersonalCodeSubmit={handlePersonalCodeSubmit}
					onPersonalCodeUpdated={() => setPersonalCodeEntryError("")}
					legalEntityId={props.initialData.legalEntityId}
					personalCodeEntryError={personalCodeEntryError}
				/>
				<StepVerification
					pin={pin}
					isWaiting={isAuthenticating}
				/>
				<SmartIdProfileUpdateStep />
				<StepSuccess
					onNavigateToProfile={props.onNavigateToProfile}
					messageKey="smart_id_add_wizard_info_page_message"
				/>
			</StepWizard>
		</SmartIdWizardErrorHandler>
	);
}

const profileUpdateStepIndex = 3;
const successStepIndex = 4;